import React, { useState, useEffect } from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { trackFunnelEvent } from '@/hooks/useFunnelTracking';
import slider1 from '@/assets/promo/slider-1.webp';
import slider2 from '@/assets/promo/slider-2.webp';
import slider3 from '@/assets/promo/slider-3.webp';
import slider4 from '@/assets/promo/slider-4.webp';

interface Slide {
  image: string;
  title: string;
  subtitle: string;
  cta: string;
  link: string;
}

const slidesContent: Record<string, Slide[]> = {
  en: [
    { image: slider1, title: 'Trusted & Reliable', subtitle: 'Premium novelty ID cards crafted with precision. Over 5 years of experience and 60,000+ satisfied customers worldwide.', cta: 'View all Models', link: '/shop' },
    { image: slider2, title: 'Premium Quality', subtitle: 'Each card features authentic-looking holograms, scannable barcodes, and PVC material identical to real ID cards.', cta: 'Explore Cards', link: '/shop' },
    { image: slider3, title: 'VIP Access', subtitle: 'Perfect for costume parties, film props, and collectibles. Join thousands who trust our premium designs.', cta: 'Get Started', link: '/shop' },
    { image: slider4, title: 'Hologram Technology', subtitle: 'Advanced holographic layers that catch the light just like genuine IDs. Unmatched attention to detail.', cta: 'See Details', link: '/shop' },
  ],
  de: [
    { image: slider1, title: 'Vertrauenswürdig & Zuverlässig', subtitle: 'Premium Novelty-Ausweise mit Präzision gefertigt. Über 5 Jahre Erfahrung und 60.000+ zufriedene Kunden weltweit.', cta: 'Alle Modelle ansehen', link: '/shop' },
    { image: slider2, title: 'Premium Qualität', subtitle: 'Jede Karte verfügt über authentisch aussehende Hologramme, scanbare Barcodes und PVC-Material identisch mit echten Ausweisen.', cta: 'Karten entdecken', link: '/shop' },
    { image: slider3, title: 'VIP-Zugang', subtitle: 'Perfekt für Kostümpartys, Filmrequisiten und Sammlerstücke. Schließe dich Tausenden an, die unseren Premium-Designs vertrauen.', cta: 'Jetzt starten', link: '/shop' },
    { image: slider4, title: 'Hologramm-Technologie', subtitle: 'Fortschrittliche holografische Schichten, die das Licht wie echte Ausweise einfangen. Unübertroffene Liebe zum Detail.', cta: 'Details ansehen', link: '/shop' },
  ],
  es: [
    { image: slider1, title: 'Confiable & Seguro', subtitle: 'Tarjetas de identificación novelty premium fabricadas con precisión. Más de 5 años de experiencia y 60.000+ clientes satisfechos.', cta: 'Ver todos los Modelos', link: '/shop' },
    { image: slider2, title: 'Calidad Premium', subtitle: 'Cada tarjeta cuenta con hologramas auténticos, códigos de barras escaneables y material PVC idéntico a las tarjetas reales.', cta: 'Explorar Tarjetas', link: '/shop' },
    { image: slider3, title: 'Acceso VIP', subtitle: 'Perfecto para fiestas de disfraces, utilería de cine y coleccionables. Únete a miles que confían en nuestros diseños premium.', cta: 'Comenzar', link: '/shop' },
    { image: slider4, title: 'Tecnología Holográfica', subtitle: 'Capas holográficas avanzadas que captan la luz como identificaciones genuinas. Atención al detalle incomparable.', cta: 'Ver Detalles', link: '/shop' },
  ],
  fr: [
    { image: slider1, title: 'Fiable & Sûr', subtitle: 'Cartes d\'identité novelty premium fabriquées avec précision. Plus de 5 ans d\'expérience et 60 000+ clients satisfaits.', cta: 'Voir tous les Modèles', link: '/shop' },
    { image: slider2, title: 'Qualité Premium', subtitle: 'Chaque carte comporte des hologrammes authentiques, des codes-barres scannables et du PVC identique aux vraies cartes.', cta: 'Explorer les Cartes', link: '/shop' },
    { image: slider3, title: 'Accès VIP', subtitle: 'Parfait pour les fêtes costumées, les accessoires de film et les objets de collection. Rejoignez des milliers de clients satisfaits.', cta: 'Commencer', link: '/shop' },
    { image: slider4, title: 'Technologie Holographique', subtitle: 'Des couches holographiques avancées qui captent la lumière comme les vraies pièces d\'identité. Un souci du détail inégalé.', cta: 'Voir les Détails', link: '/shop' },
  ],
  it: [
    { image: slider1, title: 'Affidabile & Sicuro', subtitle: 'Tessere d\'identità novelty premium realizzate con precisione. Oltre 5 anni di esperienza e 60.000+ clienti soddisfatti.', cta: 'Vedi tutti i Modelli', link: '/shop' },
    { image: slider2, title: 'Qualità Premium', subtitle: 'Ogni tessera presenta ologrammi autentici, codici a barre scansionabili e materiale PVC identico alle carte reali.', cta: 'Esplora le Carte', link: '/shop' },
    { image: slider3, title: 'Accesso VIP', subtitle: 'Perfetto per feste in costume, oggetti di scena e collezioni. Unisciti a migliaia che si fidano dei nostri design premium.', cta: 'Inizia Ora', link: '/shop' },
    { image: slider4, title: 'Tecnologia Olografica', subtitle: 'Strati olografici avanzati che catturano la luce come le carte d\'identità autentiche. Attenzione ai dettagli impareggiabile.', cta: 'Vedi Dettagli', link: '/shop' },
  ],
  pt: [
    { image: slider1, title: 'Confiável & Seguro', subtitle: 'Cartões de identidade novelty premium fabricados com precisão. Mais de 5 anos de experiência e 60.000+ clientes satisfeitos.', cta: 'Ver todos os Modelos', link: '/shop' },
    { image: slider2, title: 'Qualidade Premium', subtitle: 'Cada cartão possui hologramas autênticos, códigos de barras escaneáveis e material PVC idêntico aos cartões reais.', cta: 'Explorar Cartões', link: '/shop' },
    { image: slider3, title: 'Acesso VIP', subtitle: 'Perfeito para festas, adereços de filmes e colecionáveis. Junte-se a milhares que confiam nos nossos designs premium.', cta: 'Começar', link: '/shop' },
    { image: slider4, title: 'Tecnologia Holográfica', subtitle: 'Camadas holográficas avançadas que captam a luz como identificações genuínas. Atenção incomparável aos detalhes.', cta: 'Ver Detalhes', link: '/shop' },
  ],
  ru: [
    { image: slider1, title: 'Надёжно & Проверено', subtitle: 'Премиальные novelty ID-карты, созданные с точностью. Более 5 лет опыта и 60 000+ довольных клиентов по всему миру.', cta: 'Все Модели', link: '/shop' },
    { image: slider2, title: 'Премиум Качество', subtitle: 'Каждая карта имеет реалистичные голограммы, сканируемые штрих-коды и ПВХ-материал, идентичный настоящим картам.', cta: 'Смотреть Карты', link: '/shop' },
    { image: slider3, title: 'VIP Доступ', subtitle: 'Идеально для костюмированных вечеринок, реквизита и коллекционирования. Присоединяйтесь к тысячам довольных клиентов.', cta: 'Начать', link: '/shop' },
    { image: slider4, title: 'Технология Голограмм', subtitle: 'Продвинутые голографические слои, которые играют на свету как настоящие удостоверения. Непревзойдённое внимание к деталям.', cta: 'Подробнее', link: '/shop' },
  ],
  sv: [
    { image: slider1, title: 'Pålitligt & Säkert', subtitle: 'Premium novelty ID-kort tillverkade med precision. Över 5 års erfarenhet och 60 000+ nöjda kunder världen över.', cta: 'Visa alla Modeller', link: '/shop' },
    { image: slider2, title: 'Premiumkvalitet', subtitle: 'Varje kort har autentiska hologram, skannbara streckkoder och PVC-material identiskt med riktiga ID-kort.', cta: 'Utforska Kort', link: '/shop' },
    { image: slider3, title: 'VIP-Åtkomst', subtitle: 'Perfekt för kostymfester, filmrekvisita och samlarobjekt. Gå med tusentals som litar på våra premiumdesigner.', cta: 'Kom Igång', link: '/shop' },
    { image: slider4, title: 'Hologramteknik', subtitle: 'Avancerade holografiska lager som fångar ljuset precis som äkta legitimationer. Oöverträffad detaljnoggrannhet.', cta: 'Se Detaljer', link: '/shop' },
  ],
  zh: [
    { image: slider1, title: '值得信赖', subtitle: '精密制作的高级仿真身份卡。超过5年经验，60,000+全球满意客户。', cta: '查看所有型号', link: '/shop' },
    { image: slider2, title: '顶级品质', subtitle: '每张卡片都具有逼真的全息图、可扫描条形码和与真实ID卡相同的PVC材质。', cta: '探索卡片', link: '/shop' },
    { image: slider3, title: 'VIP通道', subtitle: '完美适用于化装派对、影视道具和收藏品。加入信赖我们高级设计的数千名客户。', cta: '立即开始', link: '/shop' },
    { image: slider4, title: '全息技术', subtitle: '先进的全息层，像真正的证件一样捕捉光线。无与伦比的细节关注。', cta: '查看详情', link: '/shop' },
  ],
};

export const PromoSlider = () => {
  const { language } = useLanguage();
  const slides = slidesContent[language] || slidesContent.en;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSlide = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToPrev = () => {
    const newIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  };

  const goToNext = () => {
    const newIndex = currentIndex === slides.length - 1 ? 0 : currentIndex + 1;
    goToSlide(newIndex);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      goToNext();
    }, 6000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  const currentSlide = slides[currentIndex];

  // Track promo view when slide changes
  useEffect(() => {
    trackFunnelEvent('promo_viewed', {
      promotion_id: `promo_slider_${currentIndex}`,
      promotion_name: currentSlide?.title,
      creative_name: `slide_${currentIndex + 1}`,
      creative_slot: `homepage_slider_${currentIndex + 1}`,
    });
  }, [currentIndex, currentSlide?.title]);

  const handlePromoClick = () => {
    trackFunnelEvent('promo_clicked', {
      promotion_id: `promo_slider_${currentIndex}`,
      promotion_name: currentSlide?.title,
      creative_name: `slide_${currentIndex + 1}`,
      creative_slot: `homepage_slider_${currentIndex + 1}`,
    });
  };

  return (
    <div className="container">
      <section className="relative h-[40vh] sm:h-[45vh] min-h-[300px] sm:min-h-[350px] max-h-[450px] overflow-hidden rounded-3xl">
      {/* Background Images */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-500 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover object-center"
            style={{ minHeight: '100%', minWidth: '100%' }}
          />
        </div>
      ))}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/60 to-transparent z-10" />
      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-background/30 z-10" />

      {/* Content */}
      <div className="absolute inset-0 z-20 flex items-center">
        <div className="container">
          <div className="max-w-xl pl-12 md:pl-16 lg:pl-20">
            <h2 
              className="font-display text-3xl md:text-4xl lg:text-5xl font-black uppercase mb-4 animate-fade-in"
              key={`title-${currentIndex}`}
            >
              {currentSlide.title.split(' ').map((word, i) => (
                <span key={i} className="text-foreground">
                  {word}{' '}
                </span>
              ))}
            </h2>
            <p 
              className="text-muted-foreground text-base mb-6 max-w-md animate-fade-in"
              key={`subtitle-${currentIndex}`}
              style={{ animationDelay: '0.1s' }}
            >
              {currentSlide.subtitle}
            </p>
            <Button 
              variant="default" 
              size="lg" 
              asChild 
              className="animate-fade-in"
              key={`cta-${currentIndex}`}
              style={{ animationDelay: '0.2s' }}
            >
              <LocalizedLink to={currentSlide.link} onClick={handlePromoClick}>
                {currentSlide.cta}
              </LocalizedLink>
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full border-2 border-foreground/30 flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300 hover:scale-110 group"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6 text-foreground/70 group-hover:text-primary-foreground transition-colors" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full border-2 border-foreground/30 flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300 hover:scale-110 group"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6 text-foreground/70 group-hover:text-primary-foreground transition-colors" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-30 flex items-center gap-0">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="p-1.5 group"
            aria-label={`Go to slide ${index + 1}`}
          >
            <span className={`block w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'w-8 bg-foreground'
                : 'bg-foreground/40 group-hover:bg-foreground/60'
            }`} />
          </button>
        ))}
      </div>

      {/* Bottom Accent Line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent z-30" />
      </section>
    </div>
  );
};
